import { useProjects } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { ProjectRole, ServicePermissionName } from '@consigli/types';
import { FC, useCallback, useMemo, useState } from 'react';
import { HiXMark } from 'react-icons/hi2';

import { Checkbox, Fieldset, Select } from '@/components';

import { ProjectAccess, servicePermissionOptions } from './types';

interface ProjectAccessFormProps {
  onAddProject: (project: ProjectAccess) => void;
  onCancel: () => void;
  existingProjects: ProjectAccess[];
  editingProject?: ProjectAccess | null;
}

export const ProjectAccessForm: FC<ProjectAccessFormProps> = ({
  onAddProject,
  onCancel,
  existingProjects,
  editingProject,
}) => {
  const { projects, isLoading } = useProjects({ page: 'all' });

  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    editingProject?.id ?? null,
  );
  const [isProjectAdmin, setIsProjectAdmin] = useState(
    editingProject ? editingProject.role === ProjectRole.ADMIN : false,
  );
  const [selectedPermissions, setSelectedPermissions] = useState<ServicePermissionName[]>(
    editingProject?.permissions ?? [],
  );

  // Filter out already added projects (except the one being edited)
  const availableProjects = useMemo(() => {
    return projects.filter(
      (p) =>
        !existingProjects.some((ep) => ep.id === p.id) ||
        (editingProject && p.id === editingProject.id),
    );
  }, [projects, existingProjects, editingProject]);

  const projectOptions = useMemo(
    () =>
      availableProjects.map((project) => ({
        label: project.name,
        value: project.id,
      })),
    [availableProjects],
  );

  const selectedProjectOption = useMemo(
    () => projectOptions.find((opt) => opt.value === selectedProjectId) ?? null,
    [projectOptions, selectedProjectId],
  );

  const handlePermissionChange = useCallback(
    (permission: ServicePermissionName, checked: boolean) => {
      setSelectedPermissions((prev) =>
        checked ? [...prev, permission] : prev.filter((p) => p !== permission),
      );
    },
    [],
  );

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedPermissions(servicePermissionOptions.map((opt) => opt.value));
    } else {
      setSelectedPermissions([]);
    }
  }, []);

  const allSelected = useMemo(
    () => selectedPermissions.length === servicePermissionOptions.length,
    [selectedPermissions],
  );

  const handleSubmit = useCallback(() => {
    if (!selectedProjectId) return;

    const selectedProject = projects.find((p) => p.id === selectedProjectId);
    if (!selectedProject) return;

    onAddProject({
      id: selectedProjectId,
      name: selectedProject.name,
      role: isProjectAdmin ? ProjectRole.ADMIN : ProjectRole.USER,
      permissions: isProjectAdmin ? [] : selectedPermissions,
    });
  }, [selectedProjectId, projects, isProjectAdmin, selectedPermissions, onAddProject]);

  const isValid = useMemo(() => {
    if (!selectedProjectId) return false;
    if (!isProjectAdmin && selectedPermissions.length === 0) return false;
    return true;
  }, [selectedProjectId, isProjectAdmin, selectedPermissions]);

  return (
    <div className="border rounded-lg p-4 bg-muted/30 space-y-4">
      {/* Header with close button */}
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm">
          {editingProject ? 'Edit Project Access' : 'Add Project Access'}
        </h4>
        <button
          type="button"
          onClick={onCancel}
          className="p-1 hover:bg-muted rounded-md text-muted-foreground hover:text-foreground"
          aria-label="Cancel"
        >
          <HiXMark className="h-4 w-4" />
        </button>
      </div>

      {/* Project Selection */}
      <div>
        <span className="block text-sm font-medium text-gray-700 mb-1">Project</span>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading projects...</p>
        ) : projectOptions.length === 0 ? (
          <p className="text-sm text-red-600">No projects available</p>
        ) : (
          <Select
            options={projectOptions}
            value={selectedProjectOption ?? undefined}
            onValueChange={(option) => setSelectedProjectId(option?.value ?? null)}
            placeholder="Select project"
            getOptionLabel={(opt) => opt?.label ?? ''}
            getOptionValue={(opt) => String(opt?.value ?? '')}
            className="w-full"
            contentClassName="max-w-[400px]"
            showSearch
          />
        )}
      </div>

      {/* Project Admin Toggle */}
      {selectedProjectId && (
        <div>
          <Checkbox
            id="projectAdmin"
            label="Project Admin"
            checked={isProjectAdmin}
            onCheckedChange={(checked) => {
              setIsProjectAdmin(checked);
              if (checked) {
                setSelectedPermissions([]);
              }
            }}
          />
          <p className="text-xs text-muted-foreground mt-1 ml-6">
            Project admins have full access to all services.
          </p>
        </div>
      )}

      {/* Service Permissions - only show if not admin */}
      {selectedProjectId && !isProjectAdmin && (
        <Fieldset legend="Service Access">
          <div className="space-y-2">
            {/* Select All */}
            <Checkbox
              id="selectAll"
              label="Select all"
              checked={allSelected}
              onCheckedChange={handleSelectAll}
            />
            <hr className="my-2" />
            {/* Individual permissions */}
            <div className="grid grid-cols-2 gap-2">
              {servicePermissionOptions.map((opt) => (
                <Checkbox
                  key={opt.value}
                  id={opt.value}
                  label={opt.label}
                  checked={selectedPermissions.includes(opt.value)}
                  onCheckedChange={(checked) => handlePermissionChange(opt.value, checked)}
                />
              ))}
            </div>
          </div>
        </Fieldset>
      )}

      {/* Action buttons */}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" size="sm" onClick={handleSubmit} disabled={!isValid}>
          {editingProject ? 'Update' : 'Add'}
        </Button>
      </div>
    </div>
  );
};
